import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { fetchDiscoverSalon } from '../../api/discover';
import BookNowLink from '../booking/BookNowLink';
import BookmarkButton from './BookmarkButton';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';

const SalonPreviewModal = ({ tenantId, open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { showToast } = useToast();
  const [tab, setTab] = useState('services');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['discover-salon', tenantId],
    queryFn: () => fetchDiscoverSalon(tenantId),
    enabled: open && !!tenantId,
  });

  const salon = data?.salon;
  const branches = data?.branches || [];
  const services = useMemo(() => (data?.services || []).slice(0, 6), [data?.services]);
  const products = useMemo(() => (data?.products || []).slice(0, 6), [data?.products]);
  const defaultBranchId = branches[0]?._id || '';

  useEffect(() => {
    if (!open) return undefined;
    setTab('services');
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleAddToCart = (product) => {
    addItem({
      productId: product._id,
      tenantId,
      name: product.name,
      price: product.price,
      imageUrl: product.images?.[0]?.url,
      quantity: 1,
    });
    showToast(t('marketplace.addedToCart'), 'success');
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="salon-preview-title"
        className="fixed left-1/2 top-1/2 z-[1101] flex max-h-[min(92vh,720px)] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-900"
      >
        <div className="relative h-36 shrink-0 bg-zinc-100 dark:bg-zinc-800">
          {salon?.coverImage ? (
            <img src={salon.coverImage} alt="" className="h-full w-full object-cover" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute right-3 top-3 flex items-center gap-2">
            <BookmarkButton tenantId={tenantId} />
            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur hover:bg-black/60"
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <h2 id="salon-preview-title" className="text-xl font-semibold text-white">
              {salon?.businessName || t('common.loading')}
            </h2>
            {salon && (
              <>
                <p className="mt-1 text-sm text-white/90">
                  {t(`salon.audience.${salon.audienceType || 'UNISEX'}`)}
                </p>
                <p className="mt-1 text-sm text-white/90">
                  ★ {salon.averageRating || '—'} · {t('explore.reviewsCount', { count: salon.totalReviews || 0 })}
                  {salon.address ? ` · ${salon.address}` : ''}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex shrink-0 gap-1 border-b border-zinc-200 px-4 pt-3 dark:border-zinc-800">
          {['services', 'products'].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`rounded-t-lg px-4 py-2 text-sm font-medium capitalize ${
                tab === key
                  ? 'border-b-2 border-glow-500 text-glow-600 dark:text-glow-300'
                  : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
              }`}
            >
              {t(`explore.tabs.${key}`)}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-zinc-500">{t('common.loading')}</p>}
          {isError && <p className="text-sm text-red-500">{t('explore.salonNotFound')}</p>}

          {!isLoading && salon && tab === 'services' && (
            <div className="space-y-3">
              {services.length === 0 ? (
                <p className="text-sm text-zinc-500">{t('marketplace.noServices')}</p>
              ) : (
                services.map((service) => (
                  <div
                    key={service._id}
                    className="flex gap-3 rounded-xl border border-zinc-200 p-3 dark:border-zinc-800"
                  >
                    {service.imageUrl && (
                      <img src={service.imageUrl} alt="" className="h-16 w-16 rounded-lg object-cover" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{service.name}</p>
                      <p className="text-xs text-zinc-500">
                        {t('common.duration', { minutes: service.duration })} · {service.category}
                      </p>
                      <p className="mt-1 text-sm font-medium text-glow-600 dark:text-glow-300">
                        {t('common.currency', { amount: Number(service.price || 0).toLocaleString() })}
                      </p>
                    </div>
                    <BookNowLink
                      tenantId={tenantId}
                      branchId={defaultBranchId}
                      serviceId={service._id}
                      className="self-center shrink-0 rounded-lg bg-glow-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-glow-500"
                    >
                      {t('common.bookNow')}
                    </BookNowLink>
                  </div>
                ))
              )}
            </div>
          )}

          {!isLoading && salon && tab === 'products' && (
            <div className="grid gap-3 sm:grid-cols-2">
              {products.length === 0 ? (
                <p className="text-sm text-zinc-500 sm:col-span-2">{t('marketplace.noProducts')}</p>
              ) : (
                products.map((product) => (
                  <div
                    key={product._id}
                    className="rounded-xl border border-zinc-200 p-3 dark:border-zinc-800"
                  >
                    {product.images?.[0]?.url && (
                      <img src={product.images[0].url} alt="" className="mb-2 h-24 w-full rounded-lg object-cover" />
                    )}
                    <p className="truncate font-medium">{product.name}</p>
                    <p className="text-sm text-glow-600 dark:text-glow-300">
                      {t('common.currency', { amount: Number(product.price || 0).toLocaleString() })}
                    </p>
                    <button
                      type="button"
                      disabled={!product.stock}
                      onClick={() => handleAddToCart(product)}
                      className="mt-2 w-full rounded-lg border border-zinc-300 py-1.5 text-xs font-medium hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-700 dark:hover:bg-zinc-800"
                    >
                      {product.stock > 0 ? t('common.addToCart') : t('marketplace.outOfStock')}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2 border-t border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-950/50">
          <BookNowLink
            tenantId={tenantId}
            branchId={defaultBranchId}
            className="flex-1 rounded-lg bg-glow-600 py-2.5 text-center text-sm font-medium text-white hover:bg-glow-500"
          >
            {t('explore.bookAppointment')}
          </BookNowLink>
          <button
            type="button"
            onClick={() => {
              onClose?.();
              navigate(`/explore/${tenantId}`);
            }}
            className="rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-medium dark:border-zinc-700"
          >
            {t('client.bookmarks.fullProfile')}
          </button>
          <Link
            to="/marketplace"
            onClick={onClose}
            className="rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-medium dark:border-zinc-700"
          >
            {t('explore.browseProducts')}
          </Link>
        </div>
      </div>
    </>,
    document.body
  );
};

export default SalonPreviewModal;
